/**
 * Live (and reattached) run view driven by a `usePlaygroundStream` session.
 * The customer-facing composition, top to bottom:
 *
 *   - a status bar with a business-readable "now" line (from the theater's
 *     beat captions) and the Cancel affordance — only while the run is live
 *   - the question card + reply form when the run pauses on HITL
 *   - the terminal deliverable card on final/error (files, summary strip)
 *   - the Run Theater (the 3D workshop acting the run out)
 *   - the reduced journey timeline (milestones, never the technical trace)
 *
 * The session's `state.log` heartbeats are adapted to TraceEvent rows via the
 * package adapter, with the terminal `final`/`error`/`awaiting` promoted in —
 * the same seam the theater and journey consume on the polled path.
 */

import type { PlaygroundResumeReply } from '@evt/flow-stream-ui'
import {
  adaptAwaiting,
  adaptError,
  adaptFinal,
  adaptHeartbeat,
  groupFanouts,
  Icon,
  Notice,
  type SessionState,
  Spinner,
  type StreamingAwaiting,
  type StreamingError,
  type StreamingFinal,
  type TraceEvent
} from '@evt/flow-stream-ui'
import { useEffect, useMemo, useState } from 'react'
import { type Messages, useI18n } from '../i18n'
import { RunTheater } from '../theater/RunTheater'
import { currentActivity, runStats } from './journey'
import { RunJourney } from './RunJourney'
import { RunQuestionCard } from './RunQuestionCard'
import { type ResultMeta, RunResult } from './RunResult'

export interface LiveRunViewProps {
  state: SessionState
  /** Round cap for the "Question N of M" pill on the question card. */
  maxRounds?: number
  /** Submit a HITL reply (drives session.respond under the hood). */
  onReply: (reply: PlaygroundResumeReply) => void
  /** Operator cancel — POSTs /api/runs/:id/cancel (not just session.cancel). */
  onCancel: () => void
  /** True while a cancel request is in flight. */
  cancelling?: boolean
  /**
   * Already-persisted trace events preceding this session — used by RunView's
   * seeded-awaiting path so the theater and journey keep the run's whole story
   * across a page reload (the fresh session log only carries resume-leg
   * events). Must be pre-adapted (the same array RunView renders itself).
   */
  priorEvents?: TraceEvent[]
}

const LIVE_STATUSES = new Set<SessionState['status']>(['running', 'awaiting', 'resuming'])

export function LiveRunView({ state, maxRounds, onReply, onCancel, cancelling, priorEvents }: LiveRunViewProps) {
  const { t } = useI18n()
  const live = LIVE_STATUSES.has(state.status)

  // 1 Hz wall-clock tick only while live, for elapsed text in the theater bar.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    if (!live) return
    const t = setInterval(() => setNowMs(Date.now()), 1_000)
    return () => clearInterval(t)
  }, [live])

  // Adapt the live log into renderable trace events, then promote terminals.
  // Persisted pre-session history (reload of an awaiting run) goes first. The
  // resume leg's LIVE stream restarts seq at 1, so shift its seqs past the
  // persisted maximum — otherwise sorting by seq interleaves the two legs.
  const events = useMemo<TraceEvent[]>(() => {
    const out: TraceEvent[] = [...(priorEvents ?? [])]
    const seqOffset = out.reduce((max, e) => (e.seq < Number.MAX_SAFE_INTEGER && e.seq > max ? e.seq : max), 0)
    for (const entry of state.log) {
      if (entry.kind !== 'heartbeat') continue
      const adapted = adaptHeartbeat(entry.record)
      out.push(adapted.seq < Number.MAX_SAFE_INTEGER ? { ...adapted, seq: adapted.seq + seqOffset } : adapted)
    }
    if (state.awaiting) {
      out.push(adaptAwaiting({ kind: 'awaiting', awaiting: state.awaiting } as StreamingAwaiting))
    }
    if (state.final) out.push(adaptFinal(state.final as StreamingFinal))
    if (state.error && 'response' in state.error) out.push(adaptError(state.error as StreamingError))
    return groupFanouts(out)
  }, [priorEvents, state.log, state.awaiting, state.final, state.error])

  // Anchor the clock at the ORIGINAL start when history is present — the
  // session's startedAtMs is the reload moment, not the run's.
  const baseMs = priorEvents?.[0]?.atMs ?? state.startedAtMs ?? events[0]?.atMs ?? nowMs

  const headline = useMemo(() => progressHeadline(state, events, t), [state, events, t])

  const finalResponse =
    state.final?.response ?? (state.error && 'response' in state.error ? state.error.response : undefined)

  const resultMeta = useMemo<ResultMeta>(() => {
    const stats = runStats(events)
    return {
      durationMs: stats.durationMs,
      questions: stats.questions,
      specialists: stats.specialists,
      sources: stats.sources
    }
  }, [events])

  return (
    <div className="live-run">
      {live && (
        <div className="live-run-bar">
          <div className="live-run-status">
            <Spinner size={15} />
            <span className="live-run-headline">{headline}</span>
          </div>
          <button type="button" className="btn btn-danger btn-sm" disabled={cancelling} onClick={onCancel}>
            {cancelling ? <Spinner size={13} /> : <Icon name="cancel" size={13} />} {t.run.cancelRun}
          </button>
        </div>
      )}

      {(state.status === 'awaiting' || state.status === 'resuming') && (
        <RunQuestionCard state={state} maxRounds={maxRounds} onSubmit={onReply} />
      )}

      {finalResponse && (
        <RunResult response={finalResponse} meta={resultMeta} executionId={state.executionId ?? undefined} />
      )}

      {state.status === 'aborted' && <Notice kind="info">{t.run.runCancelledNotice}</Notice>}

      <RunTheater key={state.executionId ?? 'pending'} events={events} baseMs={baseMs} live={live} nowMs={nowMs} />

      <RunJourney events={events} live={live} />
    </div>
  )
}

/** A friendly, human-readable progress line derived from the latest activity. */
function progressHeadline(state: SessionState, events: TraceEvent[], t: Messages): string {
  switch (state.status) {
    case 'idle':
      return t.run.headlineReady
    case 'awaiting':
      return t.run.headlineAwaiting
    case 'resuming':
      return t.run.headlineResuming
    case 'success':
      return t.run.headlineCompleted
    case 'failure':
      return t.run.headlineFailed
    case 'aborted':
      return t.run.headlineCancelled
    default:
      // running — surface the latest stage direction in business language.
      return currentActivity(events, t.beats) ?? t.run.workingFallback
  }
}
